import {
	Fragment,
	PropsWithChildren,
	ReactElement,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import { IconButton } from "../IconButton/IconButton";
import { DropOverActionList } from "../DropOverActionList/DropOverActionList";
import { AbsoluteCoordinate, DropOverAlignmentValue } from "../DropOver/DropOver";
import { ActionAndOthers } from "client_legacy/ndaw/actions";
import { actionComponentFactory, computeActionsToRender } from "../ActionList/actionsRenderHelpers";
import classNames from "classnames";
import treeAccordionItemStyles from "./TreeAccordionItem.module.less";

export type TreeAccordionActionListProps<TContext> = {
	actions: readonly ActionAndOthers<TContext>[];
	context: TContext;
	dropOverTargetAlignment?: DropOverAlignmentValue | AbsoluteCoordinate;
	dropOverContentAlignment?: DropOverAlignmentValue;
	menuTitle: string;
};

export const TreeAccordionActionList = <TContext,>(
	props: PropsWithChildren<TreeAccordionActionListProps<TContext>>
): ReactElement | null => {
	const {
		actions,
		context,
		dropOverTargetAlignment,
		dropOverContentAlignment,
		menuTitle
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const actionsMenuButton: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const actionsToRender: ActionAndOthers<TContext>[] = computeActionsToRender(actions, context);

	const closeHandler = useCallback(
		(): void => setIsOpen(false),
		[]
	);

	if (actionsToRender.length === 0) {
		return null;
	}

	return (
		<>
			<div
				className={classNames(
					treeAccordionItemStyles.actions,
					{
						[treeAccordionItemStyles.actions_visible]: isOpen
					}
				)}
			>
				<IconButton
					trackingId="more"
					icon="ellipsis"
					kind="transparent"
					onClick={() => setIsOpen(true)}
					ref={actionsMenuButton}
					bubbleClick={false}
					shape="round"
					isActive={isOpen}
				/>
			</div>
			{isOpen && actionsMenuButton.current && (
				<DropOverActionList
					target={actionsMenuButton.current}
					targetAlignment={dropOverTargetAlignment}
					contentAlignment={dropOverContentAlignment}
					onClose={closeHandler}
					bubbleClick={false}
					menuTitle={menuTitle}
				>
					{actionsToRender.map((x) => (
						<Fragment key={x.name}>
							{actionComponentFactory(x, context)}
						</Fragment>
					))}
				</DropOverActionList>
			)}
		</>
	);
};

TreeAccordionActionList.displayName = "TreeAccordion.ActionList";